import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AGENCY_PROJECT_KIND_MAP,
  CONTRACT_STATUS_MAP,
  ONBOARDING_STATUS_MAP,
  type ClientPreview,
  type ContractFinancials,
  type ContractPreview,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { date, money } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { Badge, ErrorState, Modal } from '../../components/ui';
import { Avatar, OwnerChip } from '../../components/People';
import { useAuth } from '../../auth/AuthProvider';

/**
 * معايناتُ القراءة — لمن ينفّذ لا لمن يدير.
 *
 * منفّذ المهمة يحتاج سياق العميل والعقد لا أبواب تعديلهما: النقر من شاشات
 * التنفيذ يفتح ملخّصًا للقراءة، والملف الكامل بابُه ظاهرٌ للإدارة وحدها.
 * وماليّات العقد لا تُكشف بنقرة: دورُ إدارةٍ وكلمة سرٍّ — فالشاشة قد تكون
 * أمام الفريق كله.
 */

export function ClientPreviewModal({
  accountId,
  onClose,
}: {
  accountId: string;
  onClose: () => void;
}) {
  const { atLeast } = useAuth();

  const preview = useQuery({
    queryKey: ['agency', 'client-preview', accountId],
    queryFn: () => api<ClientPreview>(`/agency/client-preview/${accountId}`),
  });

  const c = preview.data;

  return (
    <Modal title="ملخّص العميل" onClose={onClose}>
      {preview.isPending && <div className="skeleton" style={{ height: 160 }} />}
      {preview.isError && (
        <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
      )}

      {c && (
        <div className="ag-preview">
          <div className="row" style={{ gap: 'var(--sp-3)' }}>
            <Avatar name={c.name} size={48} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong style={{ fontSize: 'var(--fs-lg)' }}>{c.name}</strong>
              <p className="tiny mute">
                {[c.industry, c.city].filter(Boolean).join(' — ') || '—'}
              </p>
            </div>
            <OwnerChip ownerId={c.ownerId} size={22} />
          </div>

          <div className="ag-preview-facts">
            {c.phone && (
              <span className="ag-ctx-chip num" dir="ltr">
                {c.phone}
              </span>
            )}
            {c.email && (
              <span className="ag-ctx-chip" dir="ltr">
                {c.email}
              </span>
            )}
            <span className="ag-ctx-chip num">{c.openTasks} مهمة مفتوحة</span>
          </div>

          {c.onboarding && (
            <div className="ag-onb-inline">
              <Icon name="rocket" size={15} />
              <Badge kind={ONBOARDING_STATUS_MAP[c.onboarding.status].kind}>
                {ONBOARDING_STATUS_MAP[c.onboarding.status].label}
              </Badge>
              <span className="tiny mute num">
                {c.onboarding.done}/{c.onboarding.total} خطوة تجهيز
              </span>
            </div>
          )}

          {c.projects.length > 0 && (
            <div>
              <h4 className="label mb-4">مشاريعه</h4>
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                {c.projects.map((p) => (
                  <Link key={p.id} to={`/agency/projects/${p.id}`} className="ag-ctx-chip" onClick={onClose}>
                    <span className="ag-dot" style={{ background: p.color }} />
                    {p.name}
                    <Badge kind={AGENCY_PROJECT_KIND_MAP[p.kind]?.kind}>
                      {AGENCY_PROJECT_KIND_MAP[p.kind]?.label}
                    </Badge>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {c.retainer && (
            <div>
              <h4 className="label mb-4">باقته هذه الدورة</h4>
              {c.retainer.items.map((it) => {
                const pct = it.allowance
                  ? Math.min(100, Math.round((it.used / it.allowance) * 100))
                  : 0;
                return (
                  <div key={it.serviceId} className="ag-usage">
                    <div className="ag-usage-head">
                      <span className="small">{it.serviceName}</span>
                      <span className={`tiny num${it.over > 0 ? ' ag-late-text' : ' mute'}`}>
                        {it.used}/{it.allowance} {it.unit}
                      </span>
                    </div>
                    <div className={`ag-progress${it.over > 0 ? ' is-over' : ''}`}>
                      <span className="ag-progress-fill" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <p className="tiny mute">
            ملخّصٌ للقراءة — بلا أرقامٍ مالية. الفواتير والمستحقّات في ملفّ العميل الكامل.
          </p>

          {atLeast('MANAGER') && (
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <Link to={`/crm/accounts/${c.id}`} className="btn btn-sm" onClick={onClose}>
                <Icon name="building" size={13} />
                الملفّ الكامل (للإدارة)
              </Link>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export function ContractPreviewModal({
  contractId,
  onClose,
}: {
  contractId: string;
  onClose: () => void;
}) {
  const { atLeast } = useAuth();
  const [password, setPassword] = useState('');
  const [financials, setFinancials] = useState<ContractFinancials | null>(null);

  const preview = useQuery({
    queryKey: ['agency', 'contract-preview', contractId],
    queryFn: () => api<ContractPreview>(`/agency/contract-preview/${contractId}`),
  });

  const reveal = useMutation({
    mutationFn: () =>
      api<ContractFinancials>(`/agency/contract-preview/${contractId}/financials`, {
        method: 'POST',
        body: { password },
      }),
    onSuccess: (f) => {
      setFinancials(f);
      setPassword('');
    },
  });

  const c = preview.data;
  const st = c ? CONTRACT_STATUS_MAP[c.status as keyof typeof CONTRACT_STATUS_MAP] : null;

  return (
    <Modal title="ملخّص العقد" onClose={onClose}>
      {preview.isPending && <div className="skeleton" style={{ height: 160 }} />}
      {preview.isError && (
        <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
      )}

      {c && (
        <div className="ag-preview">
          <div className="row" style={{ gap: 'var(--sp-2)', alignItems: 'flex-start' }}>
            <Icon name="clipboard" size={20} className="mute" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong>
                <span className="num">{c.number}</span> — {c.title}
              </strong>
              <p className="tiny mute">العميل: {c.accountName}</p>
            </div>
            {st && <Badge kind={st.kind}>{st.label}</Badge>}
          </div>

          <div className="ag-preview-facts">
            <span className="ag-ctx-chip num">
              <Icon name="calendar" size={12} /> {date(c.startAt)}
              {c.endAt && <> — {date(c.endAt)}</>}
            </span>
            {c.signedAt ? (
              <span className="ag-ctx-chip">
                <Icon name="check" size={12} /> وُقّع {date(c.signedAt)}
                {c.signedByName && ` — ${c.signedByName}`}
              </span>
            ) : (
              <span className="ag-ctx-chip mute">لم يُوقَّع بعد</span>
            )}
          </div>

          {c.clauseTitles.length > 0 && (
            <div>
              <h4 className="label mb-4">بنود العقد ({c.clauseTitles.length})</h4>
              <ol className="ag-clauses">
                {c.clauseTitles.map((t, i) => (
                  <li key={i} className="small">
                    {t}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* الماليّات — مقفولة: دور إدارة + كلمة سرّ */}
          <div className="ag-locked">
            <h4 className="label mb-4">
              <Icon name="wallet" size={13} /> الأمور المالية
            </h4>
            {financials ? (
              <div>
                <p className="strong num" style={{ fontSize: 'var(--fs-lg)' }}>
                  {money(financials.value)}{' '}
                  <span className="tiny mute">{financials.currency}</span>
                </p>
                {financials.milestones.length > 0 && (
                  <ul className="ag-task-list mt-2">
                    {financials.milestones.map((m, i) => (
                      <li key={i}>
                        <div className="ag-task-row" style={{ cursor: 'default' }}>
                          <span className="ag-task-title">{m.title || `دفعة ${i + 1}`}</span>
                          <span className="num small">{money(m.amount)}</span>
                          {m.dueAt && <span className="tiny mute num">{date(m.dueAt)}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : atLeast('MANAGER') ? (
              <div>
                <p className="tiny mute mb-4">
                  الأرقام لا تُكشف بنقرة — الشاشة قد تكون أمام الفريق. أدخل كلمة سرّك لعرضها.
                </p>
                <div className="row" style={{ gap: 6 }}>
                  <input
                    type="password"
                    className="input"
                    style={{ flex: 1 }}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && password) reveal.mutate();
                    }}
                    placeholder="كلمة سرّ حسابك"
                    autoComplete="current-password"
                  />
                  <button
                    className="btn btn-primary"
                    onClick={() => reveal.mutate()}
                    disabled={!password || reveal.isPending}
                  >
                    {reveal.isPending ? 'جارٍ…' : 'إظهار'}
                  </button>
                </div>
                {reveal.isError && (
                  <p className="tiny ag-late-text mt-2">
                    {reveal.error instanceof Error ? reveal.error.message : 'تعذّر الكشف.'}
                  </p>
                )}
              </div>
            ) : (
              <p className="tiny mute">
                <Icon name="alert" size={12} /> ماليّات العقد للإدارة وحدها.
              </p>
            )}
          </div>

          {atLeast('MANAGER') && (
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <Link to={`/sales/contracts/${c.id}`} className="btn btn-sm" onClick={onClose}>
                <Icon name="clipboard" size={13} />
                العقد الكامل (للإدارة)
              </Link>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
